import { useCallback, useMemo } from 'react';

export type Tier = { points: number; label: string };

type UseProgressCalculationProps = {
  currentPoints: number;
  lastSeenLoyaltyPoints: number;
  tiers: Tier[];
};

export type ProgressData = {
  progressPercentage: number;
  pointsNeededForNextTier: number;
  currentTier: number;
};

export type AnimationState = {
  shouldAnimate: boolean;
  startValue: number;
  targetValue: number;
};

export const useProgressCalculation = ({
  currentPoints,
  lastSeenLoyaltyPoints,
  tiers,
}: UseProgressCalculationProps) => {
  const getNextTierInfo = useCallback(
    (pointsValue: number) => {
      const nextTierIndex = tiers.findIndex(cat => cat.points > pointsValue);
      const nextTierPoints = nextTierIndex !== -1 ? tiers[nextTierIndex].points : 0;
      return { nextTierIndex, nextTierPoints };
    },
    [tiers]
  );

  const calculateProgressPercentage = useCallback(
    (pointsValue: number) => {
      const categoriesLength = tiers.length;
      if (categoriesLength <= 1) {
        return 0;
      }
      const nextTierIndex = tiers.findIndex(cat => cat.points > pointsValue);
      if (nextTierIndex === -1) {
        return 100;
      }

      const currentTierIndex = Math.max(0, nextTierIndex - 1);
      const currentTierPoints = tiers[currentTierIndex].points;
      const nextTierPoints = tiers[nextTierIndex].points;

      const segmentWidth = 100 / (categoriesLength - 1);
      const denom = nextTierPoints - currentTierPoints;
      const within = denom > 0 ? (pointsValue - currentTierPoints) / denom : 0;

      let progress = (currentTierIndex + within) * segmentWidth;

      // Add a small visual floor if exactly at the minimum tier to show the progress bar
      const minPoints = tiers[0].points;
      if (pointsValue === minPoints) {
        progress = Math.max(progress, 0.1);
      }

      return Math.min(Math.max(progress, 0), 100);
    },
    [tiers]
  );

  const progressData: ProgressData = useMemo(() => {
    const { nextTierPoints, nextTierIndex } = getNextTierInfo(currentPoints);
    const pointsNeededForNextTier = nextTierPoints - currentPoints;
    const progressPercentage = calculateProgressPercentage(currentPoints);
    return {
      progressPercentage,
      pointsNeededForNextTier,
      currentTier: nextTierIndex === -1 ? tiers.length : nextTierIndex,
    };
  }, [getNextTierInfo, currentPoints, calculateProgressPercentage, tiers.length]);

  const animationState: AnimationState = useMemo(() => {
    if (!progressData) {
      return { shouldAnimate: false, startValue: 0, targetValue: 0 };
    }
    const shouldAnimate = currentPoints > lastSeenLoyaltyPoints;
    const startValue = calculateProgressPercentage(lastSeenLoyaltyPoints);
    const targetValue = progressData.progressPercentage;
    return { shouldAnimate, startValue, targetValue };
  }, [currentPoints, lastSeenLoyaltyPoints, progressData, calculateProgressPercentage]);

  return {
    progressData,
    animationState,
    calculateProgressPercentage,
    getNextTierInfo,
  };
};
